import { styled } from "goober";
import { type CSSProperties, useRef } from "react";
import {
  clampSidebarWidth,
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from "../lib/sidebarWidth";
import {
  DragIndicator,
  dragIndicatorHidden,
  dragIndicatorShown,
} from "./DragIndicator";

const Handle = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--sidebar-width) - 12px);
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  touch-action: none;
  & [data-drag-indicator] {
    ${dragIndicatorHidden("translateX(4px)")}
  }
  &:hover [data-drag-indicator],
  &:focus-visible [data-drag-indicator],
  &[data-dragging="true"] [data-drag-indicator] {
    ${dragIndicatorShown}
  }
  &:focus-visible {
    outline: none;
  }
  &:focus-visible [data-drag-indicator] {
    outline: 2px solid var(--control-bg);
    outline-offset: 3px;
  }
`;

export function SidebarResizeHandle({
  width,
  dragging,
  onResize,
  onResizeEnd,
  style,
}: {
  width: number;
  dragging: boolean;
  onResize: (width: number) => void;
  onResizeEnd: (width: number) => void;
  style?: CSSProperties;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  return (
    <Handle
      data-sidebar-region
      data-sidebar-resize
      role="separator"
      aria-label="Resize navigation"
      aria-orientation="vertical"
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      aria-valuenow={width}
      tabIndex={0}
      data-dragging={dragging}
      style={style}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = { startX: event.clientX, startWidth: width };
        onResize(width);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag) return;
        onResize(
          clampSidebarWidth(drag.startWidth + event.clientX - drag.startX)
        );
      }}
      onPointerUp={(event) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
        onResizeEnd(width);
      }}
      onDoubleClick={() => onResizeEnd(SIDEBAR_DEFAULT_WIDTH)}
      onKeyDown={(event) => {
        const step =
          event.key === "ArrowRight" ? 16 : event.key === "ArrowLeft" ? -16 : 0;
        if (!step) return;
        event.preventDefault();
        onResizeEnd(clampSidebarWidth(width + step));
      }}
    >
      <DragIndicator
        data-drag-indicator
        data-orientation="horizontal"
        aria-hidden
      />
    </Handle>
  );
}
